import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Icon,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  Typography,
  useDataTable,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import * as QUERIES from '../../../../api/candidate/result/@coordinator/queries-services/queries';
import DownloadFromS3Action from '../../../../components/buttons/downloadFromS3Action';
import { useTriggerMutate } from '../../../../hooks/query';
import { useUserDetails } from '../../../../hooks/userDetails';
import { ResultReleasesSchema } from '../../../../models/@org-admin/ResultReleasesShema';
import RouteKey from '../../../../routes/key';
import { useDownload } from './logic';
import { parseLocationSearch } from '../../../../utils/StringUtils';
import { useAuditViewResultRequestMutation } from '../../../../api/candidate/result/@candidate/queries-services/queries';

const useStyles = makeStyles()((theme) =>
({
  root: {},
  dialogContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    margin: theme.spacing(2),
  },
  titleRoot: {
    lineHeight: 1.3,
  },
  dataTableRoot: {
    width: '100%',
  },
  valueFields: {},
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
}));

/** define default columns
 * */
const dataTableColumns = (resultList: any[]): MUIDataTableColumnDef[] => [
  {
    label: 'CANDIDATE NAME',
    name: 'candidateName',
    options: {
      customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
        <Typography id={`text-candidateName-row${rowIndex}`}>
          {resultList[dataIndex].candidateName}
        </Typography>
      ),
    },
  },
  {
    label: 'NRIC/FIN/Passport No.',
    name: 'candidateId',
    options: {
      customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
        <Typography id={`text-candidateId-row${rowIndex}`}>
          {resultList[dataIndex].candidateId}
        </Typography>
      ),
    },
  },
  {
    label: 'INDEX NO.',
    name: 'indexNo',
  },
  {
    label: 'EXAM LEVEL',
    name: 'examLevel',
  },
];

const yearInitialState: { year: number } = {
  year: 0,
};

const getCustomColumn = (
  onViewDetails: (data: ResultReleasesSchema) => void,
  resultList: any[]
) => {
  const customBody = {
    label: ' ',
    name: 'search',
    options: {
      customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
        <div style={{ marginLeft: 8 }}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Button
            style={{ color: '#1473DA' }}
            id={`button-resultRelease-row${rowIndex}`}
            variant="text"
            onClick={() => {
              onViewDetails(resultList[dataIndex]);
            }}
            size="small"
          >
            View Details
          </Button>
        </div>
      ),
    },
  };
  return [...dataTableColumns(resultList), { ...customBody }];
};

const { result } = RouteKey.child;
const { coordinatorIndividualResult, coordinatorResultList } = RouteKey.result.child;

const CoordinatorResultRelease = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const [resultList, setResultList] = useState<{ year: number }>(yearInitialState);

  const { state: locationState } = useLocation<any>();
  const { code } = parseLocationSearch(locationState.code);

  const history = useHistory();
  const userDetails = useUserDetails();

  const { generic } = RouteKey.error.child;
  const { error: errorRoute } = RouteKey.child;

  /** Query to search result release
   * */
  const {
    mutate,
    isLoading,
    error: mutationError,
  } = QUERIES.usePostCoordinatorResultReleaseSearchParam(
    (response: any) => {
      if (response && response.data) {
        setResultList(response.data);

        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: response.data.page.content,
            count: response.data.page.totalElements,
          },
        } as TableActionTypes);
      }
    },
    (error: any) => null
  );

  const { state, dispatch: tableDispatch, tOptions, selectionToolbar } = useDataTable();

  const {
    openDialog,
    fileDownloadStatus,
    fileUUID,
    onBulkDownload,
    downloadTemplate,
    onDialogClose,
    // @ts-ignore
  } = useDownload(state.data);

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'multiple',
      pagination: true,
      filter: false,
      filterType: 'multiselect',
      // need to put to make selection replacement work
      selectToolbarPlacement: 'replace',
    }),
    [tOptions]
  );

  const callMutation = useCallback(() => {
    if (state.search && state.search.toString().length >= 3) {
      mutate({ page: state.page, size: state.size, keywords: state.search, examCode: code });
      return;
    }
    mutate({ page: state.page, size: state.size, keywords: '*', examCode: code });
  }, [mutate, state.page, state.search, state.size, code]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    setTriggerMutate((prev) => prev + 1);
  }, [state.search, state.page, state.size, setTriggerMutate]);

  const viewResult = useAuditViewResultRequestMutation();

  const viewResultMutation = useCallback(() => {
    viewResult.mutate();
  }, [viewResult]);

  const setTriggerResultMutate = useTriggerMutate(viewResultMutation);

  useEffect(() => {
    setTriggerResultMutate((prev) => prev + 1);
  }, [setTriggerResultMutate]);

  /** to view individual result release
   * */
  const onViewDetails = useCallback(
    (data: ResultReleasesSchema) => {
      history.push(
        `/${result}/${coordinatorResultList.route}/${coordinatorIndividualResult.route}`,
        {
          indexNo: data.indexNo,
          listingCode: locationState,
        }
      );
    },
    [history, locationState]
  );

  /** to added view result action link in table
   * */
  const renderCustomColumns = useMemo(
    () =>
      // @ts-ignore
      getCustomColumn(onViewDetails, state.data),
    [onViewDetails, state.data]
  );

  // Handle Error
  if (mutationError?.response?.status === 403) {
    history.push(`/${errorRoute}/${generic}`);
  }

  const fileDownloadProcessing =
    fileDownloadStatus === 'IDLE' || fileDownloadStatus === 'UPLOADING';
  const fileDownloadFailed = fileDownloadStatus === 'FAILED' || fileDownloadStatus === 'TIMEOUT';

  return (
    (<Grid container className={classes.root} direction="row">
      <SectionHeader title={t('exam->>ResultListing.Examinations')} />
      <Grid xs={12} container direction="row" className={classes.valueFields}>
        <Grid xs={6} sm={10} md={4}>
          <KeyValue
            id="yearOfExam"
            keyText={t('exam->>ResultListing.Year of Exam')}
            valueText={resultList.year === 0 ? '-' : `${resultList.year}`}
          />
        </Grid>
        <Grid xs={6} sm={10} md={4}>
          <KeyValue
            id="examSchool"
            keyText={t('exam->>ResultListing.Exam Centre/School')}
            valueText={userDetails?.data?.data.organisationName}
          />
        </Grid>
      </Grid>
      <Grid xs={12}>
        <Box className={classes.searchContainer}>
          <SearchBar
            placeholder="Search by Name/NRIC/FIN/Passport No./Index No."
            name="searchInput"
            ariaLabel="searchInput"
            defaultValue={state.search}
            onSearch={(str) => {
              tableDispatch({
                type: 'UPDATE_SEARCH',
                payload: str,
              });
            }}
          />
        </Box>
      </Grid>
      <Grid xs={12}>
        <MUIDataTable
          isLoading={isLoading}
          // @ts-ignore
          state={state}
          dispatch={tableDispatch}
          columns={renderCustomColumns}
          // @ts-ignore
          data={state.data}
          options={customOptions}
          components={{
            TableToolbarSelect: () =>
              selectionToolbar([
                <DownloadFromS3Action
                  key="downloadButton"
                  id="button-resultRelease-download"
                  actionType="link"
                  requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
                  label="Download"
                  onClick={(e) => {
                    onBulkDownload(
                      Object.keys(state.rowsSelected).map((i) => state.rowsSelected[i].indexNo)
                    );
                  }}
                  onDownload={(status) => downloadTemplate(status)}
                />,
              ]),
          }}
        />
      </Grid>
      <Grid xs={12}>
        <Dialog open={openDialog} className={classes.dialogContainer} maxWidth="sm">
          <DialogContent>
            <Grid container direction="column" spacing={2} alignItems="center">
              <Grid xs={12}>
                {fileDownloadProcessing && <Typography variant="h4">Processing</Typography>}
                {fileDownloadFailed && (
                  <Typography variant="h4">Failed to download, please try again later. </Typography>
                )}
              </Grid>
              {fileDownloadProcessing && (
                <Grid xs={12}>
                  <Icon
                    name={IconName.loadingIndicator}
                    size="L"
                    color={theme.color.link.linkActive02}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          {fileDownloadFailed && (
            <DialogActions>
              <Grid container justify="center">
                <Button variant="contained" onClick={onDialogClose}>
                  Ok
                </Button>
              </Grid>
            </DialogActions>
          )}
        </Dialog>
      </Grid>
    </Grid>)
  );
};
export default CoordinatorResultRelease;
